import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, Warning, CheckCircle } from "@phosphor-icons/react";

/**
 * Quick Report — paste a freeform maintenance report block. Claude parses
 * each entry (unit + issue + dated actions), matches to existing FR jobs,
 * and lets you apply the notes with one tap.
 */
export default function AdminQuickReport() {
  const [text, setText] = useState("");
  const [propertySlug, setPropertySlug] = useState("chew-flats");
  const [propertyAddress, setPropertyAddress] = useState("5346 Chew");
  const [parsing, setParsing] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [selection, setSelection] = useState({}); // proposalIdx -> {jobId, markCompleted}
  const [applying, setApplying] = useState(false);

  const parse = async () => {
    if (!text.trim()) { toast.error("Paste a report first"); return; }
    setParsing(true);
    try {
      const r = await api.post("/reports/quick-parse", {
        text, property_slug: propertySlug, property_address: propertyAddress,
      });
      setProposals(r.data.proposals || []);
      const initSel = {};
      (r.data.proposals || []).forEach((p, i) => {
        if (p.candidates?.length > 0) initSel[i] = { jobId: p.candidates[0].job_id, markCompleted: false };
      });
      setSelection(initSel);
      toast.success(`Parsed ${r.data.proposals?.length || 0} entries`);
    } catch (e) {
      toast.error("Parse failed — check EMERGENT_LLM_KEY budget");
    } finally { setParsing(false); }
  };

  const apply = async () => {
    const applies = proposals.map((p, i) => {
      const sel = selection[i];
      if (!sel?.jobId) return null;
      return { job_id: sel.jobId, actions: p.actions || [], mark_completed: !!sel.markCompleted };
    }).filter(Boolean);
    if (applies.length === 0) { toast.error("Nothing selected"); return; }
    setApplying(true);
    try {
      const r = await api.post("/reports/quick-apply", { applies });
      toast.success(`Applied to ${r.data.jobs_updated} job(s)`);
      setText(""); setProposals([]); setSelection({});
    } catch (e) {
      toast.error("Apply failed");
    } finally { setApplying(false); }
  };

  return (
    <div className="p-4 max-w-5xl mx-auto space-y-4" data-testid="quick-report-page">
      <div>
        <div className="text-[10px] font-black uppercase tracking-widest text-purple-700 flex items-center gap-1">
          <Sparkle weight="fill" size={12}/> AI-Assisted Field Log
        </div>
        <h1 className="text-2xl font-black tracking-tight">Quick Report</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Paste a freeform daily maintenance report. Claude parses each entry, matches to open FR jobs, and appends dated completion notes with one tap.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label>
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Property Slug</div>
          <input value={propertySlug} onChange={(e) => setPropertySlug(e.target.value)}
            data-testid="qr-property-slug"
            className="w-full h-9 px-2 border-2 border-slate-300 rounded font-mono text-sm"/>
        </label>
        <label>
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Property Address Hint</div>
          <input value={propertyAddress} onChange={(e) => setPropertyAddress(e.target.value)}
            data-testid="qr-property-addr"
            className="w-full h-9 px-2 border-2 border-slate-300 rounded font-mono text-sm"/>
        </label>
      </div>

      {/* Download property status PDF (routes to LaSalle Life Services for Chew, otherwise prompts) */}
      <div className="p-3 border-2 border-emerald-500 bg-emerald-50 rounded flex items-center gap-3">
        <div className="flex-1">
          <div className="text-[10px] font-black uppercase tracking-widest text-emerald-800">Office Report PDF</div>
          <div className="text-xs text-slate-700 mt-0.5">Every open + recently-completed WO for <b>{propertySlug}</b>, grouped by unit, with completion notes.</div>
        </div>
        <button data-testid="qr-download-pdf-btn"
          onClick={() => {
            const t = localStorage.getItem("token") || localStorage.getItem("fr_token") || "";
            // Smart recipient default: Chew Flats → LaSalle Life Services (Mario is their PM
            // for that portfolio). Any other property → ask.
            const isChew = /chew/i.test(String(propertySlug || "")) || /chew/i.test(String(propertyAddress || ""));
            const suggested = isChew ? "LaSalle Life Services" : "Property Manager";
            const forWhom = window.prompt("Report cover recipient", suggested) || suggested;
            const url = `${(api.defaults.baseURL || "").replace(/\/$/, "")}/reports/property-status-pdf?property_slug=${encodeURIComponent(propertySlug)}&property_title=${encodeURIComponent(propertySlug.replace(/-/g, " ").replace(/\b\w/g, s => s.toUpperCase()))}&for_whom=${encodeURIComponent(forWhom)}`;
            const a = document.createElement("a");
            a.href = url + "&_bearer=" + encodeURIComponent(t);
            // Force auth via fetch → blob so the bearer header lands
            fetch(url, { headers: { Authorization: `Bearer ${t}` } })
              .then(r => r.blob())
              .then(b => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(b);
                link.download = `${propertySlug}-status-${new Date().toISOString().slice(0,10)}.pdf`;
                link.click();
                URL.revokeObjectURL(link.href);
                toast.success(`PDF downloaded for ${forWhom}`);
              })
              .catch(() => toast.error("Download failed"));
          }}
          className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest rounded">
          📄 Download PDF
        </button>
      </div>

      <textarea value={text} onChange={(e) => setText(e.target.value)}
        data-testid="qr-textarea"
        rows={12}
        placeholder="Paste your daily field report here — units, issues, dated actions all in one blob…"
        className="w-full p-3 border-2 border-slate-300 rounded font-mono text-sm"/>

      <button onClick={parse} disabled={parsing}
        data-testid="qr-parse-btn"
        className="h-11 px-4 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-black uppercase text-xs tracking-widest rounded inline-flex items-center gap-2">
        <Sparkle weight="fill" size={14}/>
        {parsing ? "Parsing with Claude…" : "Parse Entries with Claude"}
      </button>

      {proposals.length > 0 && (
        <div className="space-y-3">
          <div className="text-sm font-black text-slate-800">Preview · {proposals.length} entries</div>
          {proposals.map((p, i) => (
            <div key={i} className="p-3 border-2 border-slate-200 bg-white rounded" data-testid={`qr-proposal-${i}`}>
              <div className="flex items-center gap-2 flex-wrap">
                {p.unit && <span className="inline-flex items-center h-5 px-2 bg-[#0f172a] text-white text-[10px] font-black uppercase tracking-widest font-mono">Unit {p.unit}</span>}
                <span className="font-black text-sm">{p.issue}</span>
              </div>
              {(p.actions || []).map((a, j) => (
                <div key={j} className="ml-3 mt-1 text-xs text-slate-700">
                  <span className="font-mono text-slate-500">{a.date || "—"}</span> · {a.note}
                </div>
              ))}
              <div className="mt-2">
                <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">Assign to FR job:</div>
                {p.candidates?.length > 0 ? (
                  <div className="space-y-1">
                    {p.candidates.map(c => (
                      <label key={c.job_id} className="flex items-center gap-2 text-xs cursor-pointer">
                        <input type="radio" name={`p${i}`} checked={selection[i]?.jobId === c.job_id}
                          onChange={() => setSelection({...selection, [i]: {...selection[i], jobId: c.job_id}})}
                          data-testid={`qr-pick-${i}-${c.job_id}`}/>
                        <span className="font-mono text-[10px] bg-slate-100 px-1 rounded">WO#{c.wo || c.job_id.slice(-6)}</span>
                        <span className="text-[10px] text-slate-500">Unit {c.unit || "—"}</span>
                        <span className="text-xs">{c.title}</span>
                        <span className="ml-auto text-[9px] text-purple-600 font-black">score {c.score}</span>
                      </label>
                    ))}
                    <label className="ml-6 flex items-center gap-2 text-[10px] cursor-pointer">
                      <input type="checkbox" checked={!!selection[i]?.markCompleted}
                        onChange={(e) => setSelection({...selection, [i]: {...selection[i], markCompleted: e.target.checked}})}
                        data-testid={`qr-complete-${i}`}/>
                      <span className="font-black uppercase tracking-widest text-emerald-700">Mark completed</span>
                    </label>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-[11px] text-amber-800">
                    <Warning weight="fill" size={12}/> No open job matched — create manually
                  </div>
                )}
              </div>
            </div>
          ))}
          <button onClick={apply} disabled={applying}
            data-testid="qr-apply-btn"
            className="h-11 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-black uppercase text-xs tracking-widest rounded inline-flex items-center gap-2">
            <CheckCircle weight="fill" size={14}/>
            {applying ? "Applying…" : "Apply Notes to Selected Jobs"}
          </button>
        </div>
      )}
    </div>
  );
}
